/**
 * The content matrix of a GROUP: what its members may do, cell by cell.
 *
 * LIFTED FROM `UserPermissionsPage`, RETARGETED, ONE THING CHANGED
 * ---------------------------------------------------------------
 * `CellToggle`, the D1 project grouping (a project-level Maestros/Files
 * row + one dashboard sub-block per report + a "Sin proyecto" bucket
 * that surfaces orphan report keys raw), the `pendingCells` map keyed
 * `R::…` / `P::…` and the single "Guardar (N)" button all survive. They
 * were expensive to get right and none of the reasons changed; only the
 * table underneath did (`group_report_permissions` /
 * `group_project_section_permissions` instead of the per-user rows).
 *
 * THE THREE-STATE CELL IS NOT DECORATION
 * --------------------------------------
 * `permission_role_defaults` stays as the layer BENEATH the group rows,
 * so "no row" is a real, distinct value from "explicit deny". Collapsing
 * to a checkbox would force writing an explicit row for every cell of
 * every group — and, worse, would make the two indistinguishable on a
 * surface where they mean opposite things across groups.
 *
 * WHAT `?? false` USED TO DO, AND WHY IT IS GONE
 * ---------------------------------------------
 * The page this comes from wrote `defaultMap.get(...) ?? false` three
 * times, so a role default that is not in the map rendered as "denied".
 * That is a silent invented value and it is reachable, not hypothetical:
 * `dashboard.send_report` role defaults exist in the database (portal
 * migration 040 seeds both roles) and were absent from the frontend's
 * vocabulary until 2026-09-07. On a per-user page the cost was
 * cosmetic; on a matrix that decides what a whole GROUP may do,
 * "denied" where the truth is "nobody knows" is the wrong answer
 * painted confidently. A missing default is UNKNOWN here: a warning
 * glyph and a tooltip naming the (role, section, action) that has no
 * row.
 *
 * AN OR ACROSS GROUPS MAKES A DENY WEAK, AND THE TOOLTIP SAYS SO
 * --------------------------------------------------------------
 * A permission resolves as OR over the person's groups. So "✕ denegado"
 * in group A is overridden by "✓" in group B — the OPPOSITE of what the
 * same glyph meant on the per-user page, where a deny was final. An
 * operator who is not told this will believe they revoked something they
 * did not, so the deny cell's own tooltip carries the sentence.
 *
 * DELETING A CELL IS NOT SETTING IT TO FALSE
 * ------------------------------------------
 * Cycling back to `default` DELETES the row, and with no row the cell
 * falls through to the person's other groups and then to the role
 * default — whereas an explicit `false` survives both. The endpoints
 * answer 200 with the whole new matrix for exactly this reason, and this
 * component re-renders from that response rather than from what it
 * sent.
 */

import { Badge, Button } from "@omnitok/ui";
import { Disclosure } from "@pervasivemind/admin-ui";
import { AlertTriangle, Save } from "lucide-react";
import { useCallback, useMemo, useState } from "react";

import { useT } from "@/i18n";
import {
  ACTIONS_BY_SECTION,
  PERMISSION_SECTIONS,
  type ProjectPermissionSection,
} from "@/lib/admin-users-api";
import type {
  GroupPermissionsOut,
  GroupProjectPermissionIn,
  GroupProjectPermissionKey,
  GroupReportPermissionIn,
  GroupReportPermissionKey,
} from "@/lib/access-api";
import { permissionActionLabel } from "@/lib/permission-labels";
import type { TFn } from "@/pages/admin-access/access-status";

export type CellState = "default" | "granted" | "denied";

/** D1: maestros/files live at project level inside project groups. */
const PROJECT_LEVEL_SECTIONS: readonly ProjectPermissionSection[] = [
  "maestros",
  "files",
];

export interface ReportPill {
  key: string;
  label: string;
  projectSlug: string | null;
}

export interface ProjectRef {
  slug: string;
  label: string;
}

/**
 * The role default beneath a cell: `true`, `false`, or **`null` for
 * "there is no row and nobody knows"**.
 *
 * The role is a parameter because `permission_role_defaults` is keyed by
 * it and a group has no role of its own — the group matrix shows the
 * `user` row, which is the one its members resolve through.
 */
export function defaultsMap(
  defaults: readonly { role: string; section: string; action: string; granted: boolean }[],
  role: string,
): Map<string, boolean> {
  const m = new Map<string, boolean>();
  for (const d of defaults) {
    if (d.role === role) m.set(`${d.section}::${d.action}`, d.granted);
  }
  return m;
}

/** `undefined` from the map means UNKNOWN. Kept as its own function so
 *  a test can assert that a missing entry never becomes `false`. */
export function defaultFor(
  map: Map<string, boolean>,
  section: string,
  action: string,
): boolean | null {
  const hit = map.get(`${section}::${action}`);
  return hit === undefined ? null : hit;
}

/** Split the pending cells into the four calls. Pure, so the test can
 *  assert that `default` DELETES rather than writing `false`. */
export function splitPending(pending: ReadonlyMap<string, CellState>): {
  reportUpserts: GroupReportPermissionIn[];
  reportDeletes: GroupReportPermissionKey[];
  projectUpserts: GroupProjectPermissionIn[];
  projectDeletes: GroupProjectPermissionKey[];
} {
  const reportUpserts: GroupReportPermissionIn[] = [];
  const reportDeletes: GroupReportPermissionKey[] = [];
  const projectUpserts: GroupProjectPermissionIn[] = [];
  const projectDeletes: GroupProjectPermissionKey[] = [];
  for (const [key, state] of pending.entries()) {
    const [ns, subject, section, action] = key.split("::");
    if (
      ns === undefined ||
      subject === undefined ||
      section === undefined ||
      action === undefined
    ) {
      continue;
    }
    if (ns === "P") {
      if (state === "default") {
        projectDeletes.push({ project_slug: subject, section, action });
      } else {
        projectUpserts.push({
          project_slug: subject,
          section,
          action,
          granted: state === "granted",
        });
      }
    } else if (state === "default") {
      reportDeletes.push({ report_key: subject, section, action });
    } else {
      reportUpserts.push({
        report_key: subject,
        section,
        action,
        granted: state === "granted",
      });
    }
  }
  return { reportUpserts, reportDeletes, projectUpserts, projectDeletes };
}

export function PermissionMatrix({
  permissions,
  reports,
  projects,
  saving,
  onSave,
}: {
  permissions: GroupPermissionsOut;
  reports: readonly ReportPill[];
  projects: readonly ProjectRef[];
  saving: boolean;
  onSave: (pending: Map<string, CellState>) => void;
}): JSX.Element {
  const { t } = useT();
  const [pending, setPending] = useState<Map<string, CellState>>(new Map());

  const reportMap = useMemo(() => {
    const m = new Map<string, boolean>();
    for (const r of permissions.reports) {
      m.set(`${r.report_key}::${r.section}::${r.action}`, r.granted);
    }
    return m;
  }, [permissions.reports]);

  const projectMap = useMemo(() => {
    const m = new Map<string, boolean>();
    for (const p of permissions.projects) {
      m.set(`${p.project_slug}::${p.section}::${p.action}`, p.granted);
    }
    return m;
  }, [permissions.projects]);

  // The `user` role's defaults: a group's members resolve through them,
  // and an admin bypasses `has_permission` entirely, so the admin row is
  // not the one this matrix explains.
  const defaults = useMemo(
    () => defaultsMap(permissions.role_defaults, "user"),
    [permissions.role_defaults],
  );

  const cellState = useCallback(
    (ns: "R" | "P", subject: string, section: string, action: string): CellState => {
      const key = `${ns}::${subject}::${section}::${action}`;
      const staged = pending.get(key);
      if (staged !== undefined) return staged;
      const stored = (ns === "R" ? reportMap : projectMap).get(
        `${subject}::${section}::${action}`,
      );
      if (stored === undefined) return "default";
      return stored ? "granted" : "denied";
    },
    [pending, reportMap, projectMap],
  );

  const setCell = useCallback(
    (
      ns: "R" | "P",
      subject: string,
      section: string,
      action: string,
      next: CellState,
    ): void => {
      setPending((prev) => {
        const m = new Map(prev);
        m.set(`${ns}::${subject}::${section}::${action}`, next);
        return m;
      });
    },
    [],
  );

  const byProject = useMemo(() => {
    const out: { project: ProjectRef; reports: ReportPill[] }[] = [];
    for (const project of projects) {
      const mine = reports.filter((r) => r.projectSlug === project.slug);
      if (mine.length > 0) out.push({ project, reports: mine });
    }
    return out;
  }, [projects, reports]);

  const orphans = useMemo(
    () =>
      reports.filter(
        (r) =>
          r.projectSlug === null ||
          !projects.some((p) => p.slug === r.projectSlug),
      ),
    [reports, projects],
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-[13px] text-neutral-600">
          {t("access.groups.content.hint")}
        </p>
        <Button
          variant="primary"
          size="sm"
          leftIcon={<Save size={14} />}
          disabled={pending.size === 0 || saving}
          onClick={() => {
            onSave(pending);
            setPending(new Map());
          }}
        >
          {t("access.groups.content.save", { count: pending.size })}
        </Button>
      </div>

      <p className="text-[13px] text-neutral-600">
        {t("access.groups.content.entitled", {
          n: permissions.entitled_report_keys.length,
        })}
      </p>

      {byProject.map(({ project, reports: mine }) => (
        <Disclosure
          key={project.slug}
          defaultOpen
          summary={project.label}
          summaryClassName="bg-neutral-50 px-4 py-2.5 text-sm"
        >
          <div className="space-y-4">
            <div className="rounded-lg border border-primary-line bg-primary-soft/40 p-3">
              <div className="flex items-center gap-2">
                <p className="text-xs font-semibold text-neutral-600">
                  {t("access.groups.content.projectLevel")}
                </p>
                <Badge variant="primary" size="sm" pill>
                  {t("access.groups.content.projectLevelBadge")}
                </Badge>
              </div>
              <p className="mt-0.5 text-xs text-neutral-500">
                {t("access.groups.content.projectSectionsHint")}
              </p>
              <div className="mt-2 space-y-3">
                {PROJECT_LEVEL_SECTIONS.map((section) => (
                  <div key={section}>
                    <p className="text-xs font-semibold text-neutral-500">
                      {section}
                    </p>
                    <div className="mt-1 flex flex-wrap gap-2">
                      {ACTIONS_BY_SECTION[section].map((action) => (
                        <CellToggle
                          key={`${section}.${action}`}
                          label={permissionActionLabel(t, action)}
                          state={cellState("P", project.slug, section, action)}
                          defaultGranted={defaultFor(defaults, section, action)}
                          section={section}
                          action={action}
                          t={t}
                          onChange={(next) =>
                            setCell("P", project.slug, section, action, next)
                          }
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {mine.map((report) => (
              <div key={report.key}>
                <p className="text-sm font-medium text-neutral-800">
                  {report.label}
                </p>
                <p className="mt-1 text-xs font-semibold text-neutral-500">
                  dashboard
                </p>
                <div className="mt-1 flex flex-wrap gap-2">
                  {ACTIONS_BY_SECTION.dashboard.map((action) => (
                    <CellToggle
                      key={`dashboard.${action}`}
                      label={permissionActionLabel(t, action)}
                      state={cellState("R", report.key, "dashboard", action)}
                      defaultGranted={defaultFor(defaults, "dashboard", action)}
                      section="dashboard"
                      action={action}
                      entitlement={action === "view"}
                      t={t}
                      onChange={(next) =>
                        setCell("R", report.key, "dashboard", action, next)
                      }
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Disclosure>
      ))}

      {orphans.length > 0 && (
        <div className="space-y-3">
          <p className="text-xs font-semibold text-neutral-500">
            {t("access.groups.content.noProject")}
          </p>
          {orphans.map((report) => (
            <Disclosure
              key={report.key}
              defaultOpen
              summary={report.label}
              summaryClassName="bg-neutral-50 px-4 py-2.5 text-sm"
            >
              <div className="space-y-3">
                {PERMISSION_SECTIONS.map((section) => (
                  <div key={section}>
                    <p className="text-xs font-semibold text-neutral-500">
                      {section}
                    </p>
                    <div className="mt-1 flex flex-wrap gap-2">
                      {ACTIONS_BY_SECTION[section].map((action) => (
                        <CellToggle
                          key={`${section}.${action}`}
                          label={permissionActionLabel(t, action)}
                          state={cellState("R", report.key, section, action)}
                          defaultGranted={defaultFor(defaults, section, action)}
                          section={section}
                          action={action}
                          entitlement={
                            section === "dashboard" && action === "view"
                          }
                          t={t}
                          onChange={(next) =>
                            setCell("R", report.key, section, action, next)
                          }
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </Disclosure>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * One three-state cell.
 *
 * Exported and pure so `__tests__/permission-matrix.test.tsx` can render
 * it: the states have to be separable with the colour removed (the
 * product owner has red-green colour blindness), and only the MARKUP
 * proves that. Four channels carry the state — the glyph, the border,
 * the weight and `aria-pressed` — and none may be dropped.
 *
 * `defaultGranted === null` is the fourth rendering and the one this
 * component exists to add: a role default with no row is UNKNOWN, drawn
 * as a warning-toned `?`, never as a denial.
 */
export function CellToggle({
  label,
  state,
  defaultGranted,
  section,
  action,
  entitlement = false,
  onChange,
  t,
}: {
  label: string;
  state: CellState;
  defaultGranted: boolean | null;
  section: string;
  action: string;
  /** Injected, so the four-channel rendering can be asserted against
   *  the markup without i18next resolving copy. */
  t: TFn;
  /** `(dashboard, view)` — the cell that OPENS a report. It is a
   *  membership gate rather than an ordinary boolean: granting the other
   *  twelve without it grants nothing at all. */
  entitlement?: boolean;
  onChange: (next: CellState) => void;
}): JSX.Element {
  const next: Record<CellState, CellState> = {
    default: "granted",
    granted: "denied",
    denied: "default",
  };
  const unknownDefault = state === "default" && defaultGranted === null;
  const colors: Record<CellState, string> = {
    default: unknownDefault
      ? "border-warning/40 bg-warning/10 text-warning-dark"
      : defaultGranted === true
        ? "border-neutral-200 bg-neutral-50 text-neutral-600"
        : "border-neutral-200 bg-white text-neutral-500",
    granted: "border-success/40 bg-success/10 text-success-dark",
    denied: "border-error/40 bg-error/5 text-error-dark",
  };
  const symbols: Record<CellState, string> = {
    default: unknownDefault ? "?" : defaultGranted === true ? "✓" : "·",
    granted: "✓",
    denied: "✕",
  };
  const title = unknownDefault
    ? t("access.groups.content.unknownDefault", { section, action })
    : state === "default"
      ? t("access.groups.content.defaultTitle", {
          value:
            defaultGranted === true
              ? t("access.groups.content.granted")
              : t("access.groups.content.denied"),
        })
      : state === "denied"
        ? // The one sentence an operator cannot learn anywhere else.
          t("access.groups.content.denyIsWeak")
        : entitlement
          ? t("access.groups.content.entitlementTitle")
          : t("access.groups.content.grantedTitle");

  return (
    <button
      type="button"
      aria-pressed={state === "granted"}
      onClick={() => onChange(next[state])}
      className={`flex items-center gap-1 rounded-md border px-2 py-1 font-mono text-xs transition-colors ${colors[state]} ${
        state === "granted" || state === "denied" ? "font-semibold" : ""
      }`}
      title={title}
    >
      <span aria-hidden="true">
        {unknownDefault ? <AlertTriangle size={12} strokeWidth={3} /> : symbols[state]}
      </span>
      <span>{label}</span>
      {entitlement && (
        <span className="text-neutral-500" aria-hidden="true">
          ★
        </span>
      )}
    </button>
  );
}
